import { trackEventInterest } from './analytics.js';
import eventsData from './data/events.json';

const DATE_OPTS = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function formatDateRange(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const sameDay = start.toDateString() === end.toDateString();

  if (sameDay) {
    return start.toLocaleDateString(undefined, DATE_OPTS);
  }
  return `${start.toLocaleDateString(undefined, DATE_OPTS)} – ${end.toLocaleDateString(undefined, DATE_OPTS)}`;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text ?? '';
  return div.innerHTML;
}

function platformLink(extraClass = '') {
  return `<span class="event-platform-link ${extraClass}">View on Event Platform <span class="material-icons" aria-hidden="true">open_in_new</span></span>`;
}

function eventMeta(dateText, venue) {
  return `
    <div class="event-meta">
      <p class="event-meta-row">
        <span class="material-icons" aria-hidden="true">calendar_today</span>
        <span>${escapeHtml(dateText)}</span>
      </p>
      ${
        venue
          ? `<p class="event-meta-row">
        <span class="material-icons" aria-hidden="true">place</span>
        <span>${escapeHtml(venue)}</span>
      </p>`
          : ''
      }
    </div>
  `;
}

function eventCard(event, { large = false } = {}) {
  const image = event.image
    ? `<img src="${escapeHtml(event.image)}" alt="" class="event-card-image" loading="lazy">`
    : `<div class="event-card-image event-card-image--placeholder"><span class="material-icons">event</span></div>`;

  const tags =
    event.tags?.length > 0
      ? `<div class="event-card-tags">${event.tags
          .slice(0, 3)
          .map((t) => `<span class="event-tag">${escapeHtml(t)}</span>`)
          .join('')}</div>`
      : '';

  return `
    <a href="${escapeHtml(event.url)}" target="_blank" rel="noopener noreferrer"
       class="event-card google-card ${large ? 'event-card--large' : ''}"
       data-event-id="${event.id}">
      ${image}
      <div class="event-card-body">
        ${event.eventType ? `<p class="event-card-type">${escapeHtml(event.eventType)}</p>` : ''}
        <h3 class="event-card-title">${escapeHtml(event.title)}</h3>
        ${eventMeta(formatDateRange(event.startDate, event.endDate), event.venue)}
        ${event.descriptionShort ? `<p class="event-card-desc">${escapeHtml(event.descriptionShort)}</p>` : ''}
        ${tags}
        ${platformLink()}
      </div>
    </a>
  `;
}

function devfestFeatured(event) {
  if (!event) return '';

  const yearBadge = event.year ? `<span class="devfest-year-badge">${event.year}</span>` : '';

  return `
    <article class="devfest-featured google-card">
      <div class="devfest-featured-media">
        ${yearBadge}
        ${
          event.image
            ? `<img src="${escapeHtml(event.image)}" alt="" loading="lazy">`
            : '<div class="event-card-image--placeholder devfest-featured-placeholder"><span class="material-icons">festival</span></div>'
        }
      </div>
      <div class="devfest-featured-body">
        <p class="devfest-label">Keynote event of the year</p>
        <h3 class="devfest-featured-title">${escapeHtml(event.title)}</h3>
        ${eventMeta(formatDateRange(event.startDate, event.endDate), event.venue)}
        <p class="devfest-featured-desc">${escapeHtml(event.descriptionShort)}</p>
        <a href="${escapeHtml(event.url)}" target="_blank" rel="noopener noreferrer"
           class="cta-button devfest-cta" data-event-id="${event.id}">
          <span class="material-icons" aria-hidden="true">open_in_new</span>
          View on Event Platform
        </a>
      </div>
    </article>
  `;
}

function emptyState(message) {
  return `<p class="events-empty text-gray-600 text-center col-span-full">${escapeHtml(message)}</p>`;
}

function renderEvents(data) {
  const devfestFeaturedEl = document.getElementById('devfest-featured');
  const devfestArchiveEl = document.getElementById('devfest-archive');
  const upcomingEl = document.getElementById('events-upcoming');
  const pastEl = document.getElementById('events-past');

  if (!devfestFeaturedEl || !upcomingEl || !pastEl) {
    return;
  }

  const featured = data.featuredDevfest;
  const archiveDevfests = (data.devfests || []).filter((e) => e.id !== featured?.id);

  devfestFeaturedEl.innerHTML = featured
    ? devfestFeatured(featured)
    : emptyState('DevFest details coming soon.');

  if (devfestArchiveEl) {
    if (archiveDevfests.length > 0) {
      devfestArchiveEl.innerHTML = `
        <div class="devfest-archive-header col-span-full">
          <h3 class="devfest-archive-title">Previous DevFests</h3>
          <p class="devfest-archive-subtitle">Explore highlights from past flagship events</p>
        </div>
        ${archiveDevfests.map((e) => eventCard(e)).join('')}
      `;
    } else {
      devfestArchiveEl.innerHTML = '';
    }
  }

  upcomingEl.innerHTML =
    data.upcoming?.length > 0
      ? data.upcoming.map((e) => eventCard(e)).join('')
      : emptyState('No upcoming events right now. Check the event platform for new listings.');

  pastEl.innerHTML =
    data.past?.length > 0
      ? data.past.map((e) => eventCard(e)).join('')
      : emptyState('No past events to show.');

  document.querySelectorAll('[data-event-id]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = el.getAttribute('data-event-id');
      const title =
        el.querySelector('.event-card-title, .devfest-featured-title')?.textContent?.trim() ||
        `event_${id}`;
      trackEventInterest(title.toLowerCase().replace(/\s+/g, '_'));
    });
  });
}

export function loadEvents() {
  try {
    renderEvents(eventsData);
  } catch (err) {
    console.error('Failed to render events:', err);
    const msg = 'Could not load events. Run npm run sync-events, then restart dev or rebuild.';
    const devfestFeaturedEl = document.getElementById('devfest-featured');
    const upcomingEl = document.getElementById('events-upcoming');
    if (devfestFeaturedEl) devfestFeaturedEl.innerHTML = emptyState(msg);
    if (upcomingEl) upcomingEl.innerHTML = emptyState(msg);
  }
}
